import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { hasContent } from 'my-core/editor-utils';
import { useLocalStorage, useSiteTourStep } from 'my-core/hooks';
import { SITE_TOURS } from 'my-core/site-tour';

import ActivityQuestions from 'my-components/ActivityQuestions';
import ActivityVideoPlayer from 'my-components/ActivityVideoPlayer';
import MultiColumnContentsWrapper, {
  MULTI_COLUMN_DEFAULT_BREAKPOINT,
} from 'my-components/OnlineCourse/InCourseExperience/MultiColumnContentsWrapper';

import CourseLessonBlocked from './CourseLessonBlocked';
import CourseLessonLecture from './CourseLessonLecture';
import CourseLessonVideoTransition from './CourseLessonVideoTransition';

import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';

import { faObjectsColumn, faRectangleWide } from '@fortawesome/pro-regular-svg-icons';

const rootCss = ({ breakpoints, palette, shape: { borderRadius }, spacing }) => ({
  '.CL-paper': {
    backgroundColor: palette.background.paper,
    overflow: 'hidden',
    border: `1px solid ${palette.divider}`,
    borderRadius,
    '+ .CL-paper': { marginTop: spacing(1) },
  },
  '.CL-videoWrapper': { position: 'relative' },
  '.CL-lecture': {
    padding: spacing(4),
    overflow: 'visible',
  },
  '.CL-videoSkeleton': { display: 'block', width: '100%', aspectRatio: '16 / 9' },

  '*:fullscreen .CL-theatreModeIcon': { display: 'none' },

  [breakpoints.only('xs')]: {
    '.CL-lecture': { padding: spacing(2) },
  },
});

const TOKEN_PRICE = 1;
const PREVIEW_MAX_PERCENT = 20;
const PREVIEW_MAX_SECONDS = 5 * 60;

export default function CourseLesson({
  activityTitle,
  assignmentId,
  autoplay,
  canView,
  courseId,
  courseLesson,
  courseSubscription,
  eventTrackingProperties,
  fetching,
  fetchingBooklet,
  hideEmptyVideo,
  hideQuestionForm,
  onCompleted,
  onGoToNext,
  onlineCourse,
  onlineCourseId,
  onlineCourseUser,
  onPrintBooklet,
  onPurchase,
  // onShowFeedback,
  // onStartTrial,
  onSaveProgress,
  onTogglePlaying,
  onTokenPurchase,
  onViewingChange,
  playing,
  progress,
  reviewMode,
  slots,
  subscription,
  tokenBalance,
  // subscriptionTrialStatus,
  tokenPurchaseStatus,
  userCourse,
  videoDrmId,
}) {
  const { setValue: setAltLayout, value: forceSingleColumn } = useLocalStorage('in_course_experience_alt_layout');
  const multiColumnLayoutEnabled = useMediaQuery(({ breakpoints }) => breakpoints.up(MULTI_COLUMN_DEFAULT_BREAKPOINT));

  useEffect(() => {
    if (fetching || !(courseLesson?.reading_only || !courseLesson?.has_video) || !onCompleted) return;

    let timeoutCalled = false;
    const timeout = setTimeout(() => {
      timeoutCalled = true;
      onCompleted();
    }, 5000);
    return () => {
      if (timeoutCalled) return;
      clearTimeout(timeout);
      onCompleted();
    };
  }, [fetching, courseLesson?.reading_only, courseLesson?.has_video, onCompleted]);

  function handleVideoEnded() {
    if (autoplay && onGoToNext) {
      // use setTimeout so event handling can finish up
      setTimeout(onGoToNext, 0);
    }
  }

  const canTokenPurchase = tokenBalance >= TOKEN_PRICE;
  const startPosSecs = useMemo(
    () => courseLesson?.elements?.find(e => e.type === 'start')?.time || 0,
    [courseLesson?.elements],
  );
  const maxPreviewSecs = PREVIEW_MAX_SECONDS + startPosSecs;
  const maxPreviewPct = PREVIEW_MAX_PERCENT + (startPosSecs / courseLesson?.video?.duration || 0);

  const autoPurchaseToken = useCallback(
    progress => {
      if (progress.played > maxPreviewPct / 100 || progress.playedSeconds > maxPreviewSecs) {
        onTokenPurchase();
      }
    },
    [onTokenPurchase, maxPreviewSecs, maxPreviewPct],
  );

  // `onViewingChange` writes to a ref on the caller's side rather than to state, which is what makes
  // it safe to call from `onProgress` several times a second.
  const savedProgressRef = useRef(progress);
  savedProgressRef.current = progress;

  const reportViewing = useCallback(
    positionSecs => onViewingChange?.({ position_secs: Math.round(positionSecs) }),
    [onViewingChange],
  );

  // Seeded from the resume position so a message sent before they press play still says where they
  // are, and read out of a ref so a save landing mid-lesson cannot re-run this and drag the
  // reported position back to the last one written.
  // A layout effect, so its clear on the way out lands before a practice question taking its place reports from
  // componentDidMount; a useEffect cleanup would run after that and wipe the report.
  useLayoutEffect(() => {
    if (!onViewingChange || !courseLesson?.id) return undefined;

    reportViewing((savedProgressRef.current?.played || 0) * (courseLesson.video?.duration || 0));
    return () => onViewingChange(null);
  }, [courseLesson?.id, courseLesson?.video?.duration, onViewingChange, reportViewing]);

  const shouldAutoPurchaseToken =
    !canView && canTokenPurchase && !(tokenPurchaseStatus?.error || tokenPurchaseStatus?.fetching);
  const handleProgress = useCallback(
    progress => {
      reportViewing(progress.playedSeconds);
      if (shouldAutoPurchaseToken) {
        autoPurchaseToken(progress);
      }
    },
    [autoPurchaseToken, reportViewing, shouldAutoPurchaseToken],
  );

  const [previewEnded, setPreviewEnded] = useState(false);
  // after token / subscription purchase, canView will change
  useEffect(() => {
    if (canView) setPreviewEnded(false);
  }, [canView]);

  const lectureComponent = !!(hasContent(courseLesson?.content) || fetching) && (
    <CourseLessonLecture
      className={classNames('CL-paper', 'CL-lecture')}
      content={courseLesson?.content}
      courseSubscription={courseSubscription}
      enableCopy={reviewMode}
      eventTrackingProperties={eventTrackingProperties}
      fetching={fetching}
      fetchingBooklet={fetchingBooklet}
      onlineCourse={onlineCourse}
      onlineCourseUser={onlineCourseUser}
      onPrintBooklet={onPrintBooklet}
      onPurchase={onPurchase}
      onTokenPurchase={onTokenPurchase}
      // previewMode={!canView}
      showSolutions={reviewMode}
      subscription={subscription}
      // subscriptionTrialStatus={subscriptionTrialStatus}
      tokenPrice={TOKEN_PRICE}
      tokenPurchaseStatus={tokenPurchaseStatus}
      // onStartTrial={onStartTrial}
      userCourse={userCourse}
    />
  );

  const questionsComponent = !!((onlineCourse || onlineCourseId) && courseLesson) && (
    <div className={classNames('CL-paper', 'CL-lecture')}>
      <ActivityQuestions
        activity={courseLesson}
        assignmentId={assignmentId}
        courseId={courseId}
        enableTour
        eventTrackingProperties={eventTrackingProperties}
        hideQuestionForm={hideQuestionForm}
        onlineCourse={onlineCourse}
        onlineCourseId={onlineCourseId}
        onPurchase={onPurchase}
        subscription={subscription}
      />
    </div>
  );

  const videoPlayerEnabled = !previewEnded || (courseLesson && !courseLesson?.has_video);
  const lectureOnly = !!(courseLesson?.reading_only || (hideEmptyVideo && !fetching && !courseLesson?.video));
  const layoutToggleEnabled = !!(
    multiColumnLayoutEnabled &&
    lectureComponent &&
    courseLesson?.has_video &&
    !lectureOnly
  );
  const { id: tourId, steps } = SITE_TOURS.ONLINE_COURSE_EXPERIENCE;
  const layoutBtnId = useSiteTourStep({
    disabled: !videoPlayerEnabled || !layoutToggleEnabled,
    step: steps.layout,
    tourId,
  });

  if (lectureOnly) {
    if (!lectureComponent) return null;
    return (
      <MultiColumnContentsWrapper css={rootCss} variant={forceSingleColumn ? 'singleColumn' : 'default'}>
        <div>{lectureComponent}</div>
        {!!questionsComponent && <div>{questionsComponent}</div>}
      </MultiColumnContentsWrapper>
    );
  }

  const questionsBelowVid = !!(lectureComponent && !forceSingleColumn && multiColumnLayoutEnabled);
  return (
    <MultiColumnContentsWrapper
      css={rootCss}
      variant={forceSingleColumn || !lectureComponent ? 'singleColumn' : 'default'}
    >
      <div>
        {videoPlayerEnabled ?
          renderVideoComponent()
        : <CourseLessonBlocked
            className="CL-paper"
            courseLesson={courseLesson}
            courseSubscription={courseSubscription}
            eventTrackingProperties={eventTrackingProperties}
            onlineCourse={onlineCourse}
            onlineCourseUser={onlineCourseUser}
            onPurchase={onPurchase}
            // onStartTrial={onStartTrial}
            onTokenPurchase={onTokenPurchase}
            previewEnded={previewEnded}
            slots={slots}
            subscription={subscription}
            // subscriptionTrialStatus={subscriptionTrialStatus}
            tokenPrice={TOKEN_PRICE}
            tokenPurchaseStatus={tokenPurchaseStatus}
            userCourse={userCourse}
          />
        }
        {questionsBelowVid && questionsComponent}
      </div>
      {[lectureComponent, questionsComponent].some(Boolean) && (
        <div>
          {lectureComponent}
          {!questionsBelowVid && questionsComponent}
        </div>
      )}
    </MultiColumnContentsWrapper>
  );

  function renderVideoComponent() {
    return (
      <>
        <div className={classNames('CL-paper', 'CL-videoWrapper')}>
          {!reviewMode && (fetching || courseLesson?.video) && <CourseLessonVideoTransition message={activityTitle} />}
          <ActivityVideoPlayer
            autoplay={autoplay}
            elements={courseLesson?.elements}
            enableTour
            eventTrackingProperties={eventTrackingProperties}
            extraControls={
              layoutToggleEnabled &&
              [
                {
                  className: 'CL-theatreModeIcon',
                  icon: forceSingleColumn ? faObjectsColumn : faRectangleWide,
                  onClick: () => setAltLayout(alt => !alt),
                  tooltip: forceSingleColumn ? 'Columns orientation' : 'Theatre orientation',
                  id: layoutBtnId,
                },
              ].filter(Boolean)
            }
            fetching={fetching}
            maxProgressPct={!canView && !canTokenPurchase && maxPreviewPct}
            maxProgressSecs={!canView && !canTokenPurchase && maxPreviewSecs}
            onCompleted={onCompleted}
            onMaxProgressReached={() => setPreviewEnded(true)}
            onProgress={handleProgress}
            onSaveProgress={onSaveProgress}
            onTogglePlaying={onTogglePlaying}
            onVideoEnded={handleVideoEnded}
            playing={playing}
            progress={progress}
            reviewMode={reviewMode}
            video={courseLesson?.video}
            videoDrmId={videoDrmId}
          />
        </div>
      </>
    );
  }
}

export function CourseLessonSkeleton() {
  return (
    <MultiColumnContentsWrapper css={rootCss}>
      <div>
        <Skeleton className={classNames('CL-paper', 'CL-videoSkeleton')} variant="rounded" />
      </div>
      <div>
        <div className={classNames('CL-paper', 'CL-lecture')}>
          <Skeleton width="45%" />
          <Skeleton />
          <Skeleton width="88%" />
          <Skeleton width="64%" />
          <Skeleton width="78%" />
        </div>
      </div>
    </MultiColumnContentsWrapper>
  );
}
